import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import React, { useCallback, useEffect, useState } from 'react';
import { Image, Platform, Pressable, ScrollView, View, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAppDispatch } from '../../hooks/store-hook';
import useTranslation from '../../hooks/translation-hook';
import { areMissionReferralPopupAssetsReady, preloadMissionReferralPopupAssets } from '../../helpers/popupAssetPreload';
import { lightBoxActions } from '../../store/lightBoxSlice';
import Label from '../common/Label';
import Loading from '../common/Loading';

export interface MissionReferralPopupProps {
  referrerFirstName?: string;
  onClose?(): void;
  onViewMission?(): void;
}

const referralHeaderAspectRatio = 350 / 235;

const MissionReferralPopup = (props: MissionReferralPopupProps) => {
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const layout = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const [imagesReady, setImagesReady] = useState<boolean>(areMissionReferralPopupAssetsReady());
  const nativeBottomInset = Platform.OS === 'android' ? Math.max(insets.bottom, 0) : 0;
  const popupWidth = Math.min(350, Math.max(layout.width - 32, 280));
  const popupMaxHeight = Math.max(layout.height - 40 - nativeBottomInset, 260);
  const headerHeight = Math.round(popupWidth / referralHeaderAspectRatio);
  const purple = '#6253ad';
  const titleColor = '#ffb21b';
  const ctaColor = '#ffa51f';
  const webNoDragStyle = Platform.OS === 'web' ? ({ userSelect: 'none' } as any) : undefined;
  const referrerFirstName = props.referrerFirstName?.trim() || t('mission-referral-popup-default-first-name');

  const handleClose = useCallback(() => {
    dispatch(lightBoxActions.setVisibility(false));
    dispatch(lightBoxActions.setOnClose(undefined));
    props.onClose?.();
  }, [dispatch, props.onClose]);

  const handleViewMission = useCallback(() => {
    dispatch(lightBoxActions.setVisibility(false));
    dispatch(lightBoxActions.setOnClose(undefined));
    props.onViewMission?.();
  }, [dispatch, props.onViewMission]);

  useEffect(() => {
    dispatch(lightBoxActions.setOnClose(handleClose));
  }, [dispatch, handleClose]);

  useEffect(() => {
    let mounted = true;

    preloadMissionReferralPopupAssets().then(() => {
      if (mounted) {
        setImagesReady(true);
      }
    });

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <ScrollView
      style={[{ flexGrow: 0, width: popupWidth, maxHeight: popupMaxHeight, backgroundColor: purple }, webNoDragStyle]}
      contentContainerStyle={{ backgroundColor: purple }}
      nestedScrollEnabled={true}
      scrollEnabled={true}
      bounces={false}
      alwaysBounceVertical={false}
      alwaysBounceHorizontal={false}
      overScrollMode="never"
    >
      <View style={[{ width: popupWidth, minHeight: 480, backgroundColor: purple, overflow: 'hidden' }, webNoDragStyle]}>
        {imagesReady ? (
          <Image
            {...({ pointerEvents: 'none' } as any)}
            source={require('../../../assets/referral-pop-header.png')}
            style={{ width: popupWidth, height: headerHeight, resizeMode: 'contain' }}
          />
        ) : (
          <View style={{ width: popupWidth, height: headerHeight, backgroundColor: purple }}>
            <Loading style={{ backgroundColor: 'transparent' }} />
          </View>
        )}

        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t('close')}
          onPress={handleClose}
          hitSlop={12}
          style={{ position: 'absolute', top: 10, right: 10, zIndex: 2, width: 32, height: 32, alignItems: 'center', justifyContent: 'center' }}
        >
          <FontAwesomeIcon icon={faXmark} size={22} color="#000000" />
        </Pressable>

        <View style={{ paddingHorizontal: 28, paddingTop: 18, paddingBottom: 36, alignItems: 'center' }}>
          <Label style={{ color: titleColor, fontSize: 26, lineHeight: 30, fontWeight: '700', textAlign: 'center' }} numberOfLines={3}>
            {t('mission-referral-popup-title')}
          </Label>
          <Label style={{ marginTop: 20, color: '#ffffff', fontSize: 18, lineHeight: 22, textAlign: 'center' }} numberOfLines={5}>
            {t('mission-referral-popup-body', { firstName: referrerFirstName })}
          </Label>
          <Pressable
            accessibilityRole="button"
            onPress={handleViewMission}
            style={{
              minWidth: 160,
              minHeight: 34,
              marginTop: 22,
              paddingHorizontal: 24,
              borderRadius: 17,
              backgroundColor: ctaColor,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Label style={{ color: '#000000', fontSize: 14, lineHeight: 18, fontWeight: '700' }} numberOfLines={1}>
              {t('mission-referral-popup-cta')}
            </Label>
          </Pressable>
        </View>
      </View>
    </ScrollView>
  );
};

export default MissionReferralPopup;
